/**
 * Creating a task, as a command (IR-126 §4.3, phases 4.1a and 5.2).
 *
 * **It asks the server to make the task, and waits to hear whether it did.**
 * The browser no longer builds the task itself.
 *
 * Two ways in, one machinery: {@link submitTaskCreate} approves a proposal, and
 * {@link submitManualTaskCreate} creates one by hand. They differ in exactly two
 * things — where the id comes from and whether content is sent — and share
 * everything about how an in-flight create is tracked and reported.
 *
 * ## Why this is a sibling of `_submitFieldEdit`, not a variant of it
 *
 * Four things the edit path does, a create structurally cannot:
 *
 * - it addresses the command through `task.project_task_ref` — there is no task
 *   yet to read one from;
 * - it reserves a `base_seq` from the ledger — `TaskCreateCommand` has no
 *   `base_seq` field at all, because there is no prior revision to be stale
 *   against (§4.3);
 * - it joins the chain, so one failure can discard its successors — two accepts
 *   of different proposals are independent, not a chain;
 * - it maps `stale` to the conflict dialog — `stale` is unreachable without a
 *   `base_seq`.
 *
 * Sharing one function would mean a meaningless sequence number and a chain
 * entry that can never be discarded. So the machinery below is deliberately
 * separate — while `submitTaskCommand`, `reportOutcome`, the pending state and
 * the recovery pointers are all the same ones the edit path uses.
 *
 * ## Nothing is applied optimistically, and that is the design
 *
 * There is no local value to change. The proposal flips when its own snapshot
 * delivers `review_decision: accepted` and `project_task_ref`; the board row
 * appears when the ProjectTask snapshot delivers it. Both are written by
 * `apply_create` in **one transaction**, so both arrive together.
 *
 * Fabricating the board row client-side was considered and rejected: a
 * client-built row differs from the derived one in ways nobody notices until
 * the trail disagrees with the board, and "genesis is a derivation, not a
 * client claim" is the property this whole write path exists to establish.
 *
 * So there is no `apply`/`rollback` pair here. What the user gets instead is a
 * pending mark, which is the honest statement: this is in flight.
 *
 * ## An approve sends no content; a manual create is nothing but content
 *
 * Approving carries `proposal_ref` and nothing else of substance. `apply_create`
 * reads the proposal **inside its transaction** and derives the task from it;
 * `content` alongside a `proposal_ref` is ignored, not merged. The ~30-field
 * object the browser used to assemble is gone rather than moved.
 *
 * A manual create has no proposal to derive from, so its content is the whole
 * payload — and its `seq 0` claims only "the human's first content", which is
 * exactly what it is (§4.3). It is validated field by field against the same
 * `EditPolicy` allow-list that governs edits, server-side, so the browser
 * cannot widen what a human may author by sending more keys.
 */
import { logger } from '@/utils/logger'
import { submitTaskCommand, isNotQueued } from './useTaskCommands'
import type { TaskCreatePayload } from './useTaskCommands'
import { reportOutcome } from './useTaskCommandErrors'
import { forgetCommand, rememberCommand } from './taskCommandPointers'
import { markPending, releasePending } from './useTaskPending'
import { useAppStore } from '@/stores/app'
import i18n from '@/plugins/i18n'

const t = i18n.global.t

/**
 * Namespace marker for a hand-created id — mirrors `MANUAL_TASK_ID_PREFIX` in
 * `core_models_services` (`task_edit/apply.py`).
 *
 * Duplicated rather than imported because there is no shared module across the
 * Python/TypeScript boundary, and pinned by a test that states the constant the
 * apply layer refuses against. A drift here is not cosmetic: the apply layer
 * refuses `INVALID_TASK_ID` for any manual id without it, so every manual create
 * would fail with an error the user cannot act on.
 */
export const MANUAL_TASK_ID_PREFIX = 'manual_'

/**
 * A fresh id for a task created by hand (§5.6).
 *
 * **The prefix is structural, not cosmetic.** A manual id must not be able to
 * collide with a proposal id, and checking a candidate against existing
 * proposals cannot achieve that — the proposal may be created *after* the check.
 * Without separation, a manual create can squat on the id of a not-yet-approved
 * proposal (nothing is there, so the create guard passes), and approving that
 * proposal later fails permanently with `ALREADY_CREATED`, breaking the
 * inherited-id correspondence for good (§7.3).
 *
 * Minted client-side, like every other `task_id`: that is load-bearing in three
 * places (§5.6) — the create guard's double-click protection, optimistic
 * rendering, and why an edit can overtake its own create. Do not "fix" it by
 * minting server-side.
 *
 * A UUID's charset (`[0-9a-f-]`) sits inside the apply layer's
 * `[A-Za-z0-9_-]` allow-list, and 43 characters is well under its 200 cap, so
 * the id this returns is accepted by construction rather than by luck.
 */
export function mintManualTaskId(): string {
  return `${MANUAL_TASK_ID_PREFIX}${randomToken()}`
}

/**
 * Cryptographic randomness, with no `Math.random()` path.
 *
 * Deliberate: a weak id can collide with another member's manual task in the
 * same project, and the failure that produces — `ALREADY_CREATED` on a task the
 * user just typed — is both confusing and unrecoverable without retyping.
 * Better to fail loudly on a browser that cannot do this than to mint an id
 * that is probably unique.
 */
function randomToken(): string {
  const c = globalThis.crypto
  if (c?.randomUUID) return c.randomUUID()
  if (c?.getRandomValues) {
    const bytes = c.getRandomValues(new Uint8Array(16))
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  }
  throw new Error('cannot mint a task id: no crypto source available')
}

export interface TaskCreateArgs {
  /** The proposal document being approved. Becomes `proposal_ref`. */
  proposalPath: string
  /**
   * The id the task will have.
   *
   * Known before submitting because the task **inherits the proposal's id**
   * (§7.3) — which is what lets the pending mark and the recovery pointer be
   * keyed correctly while the task does not yet exist.
   */
  taskId: string
  projectId: string
  /**
   * Run once the task actually exists, with its path.
   *
   * Everything that used to follow `batch.commit()` hangs off this: the TraceX
   * auto-export, the CSM events, the aggregate recount. On `done` the apply
   * layer has committed, so the task is really there — the same guarantee the
   * batch gave, reached a different way.
   */
  onCreated?: (taskPath: string) => void
  /**
   * Give up waiting after this long. Defaults to the shared command timeout.
   *
   * Exists mainly so tests can reach the `unresolved` branch without waiting
   * fifteen real seconds — the same seam `RecoverOptions` carries.
   */
  timeoutMs?: number
  /**
   * MYAC-1639 — create the row's hidden `parked` task instead of accepting it:
   * the first comment or edit on an action nobody has added yet needs a task to
   * live on. `already_created` converges here exactly as for an accept.
   */
  parked?: boolean
  /**
   * Run when the answer was `already_created` — the task existed before this
   * create (MYAC-1639). The create still resolves `true`; this is for a caller
   * that must then learn what state that task is in.
   */
  onConverged?: () => void
}

/**
 * Approve a proposal. Resolves `true` when the task exists.
 *
 * Never throws: a failure is reported to the user through §4.5's dispatch and
 * returned as `false`.
 */
export async function submitTaskCreate(args: TaskCreateArgs): Promise<boolean> {
  return runCreate(
    {
      kind: 'task_create',
      task_id: args.taskId,
      proposal_ref: args.proposalPath,
      ...(args.parked ? { parked: true as const } : {}),
    },
    args.taskId,
    args,
  )
}

/** What a hand-created task starts life with. */
export interface ManualTaskCreateArgs {
  projectId: string
  /**
   * Keyed by the stored leaf paths `EditPolicy` names — `name`, `description`,
   * `context.finding`, `context.risk` — the same vocabulary a `task_edit`
   * carries, because it is validated against the same allow-list.
   *
   * May be empty: a create with no content is a sparse task, not a malformed
   * command, and the apply layer allows it explicitly. The dialog still asks for
   * a title, because a board full of untitled rows is a different problem.
   */
  content: Record<string, unknown>
  onCreated?: (taskPath: string) => void
  timeoutMs?: number
}

/**
 * Create a task by hand. Resolves `true` when it exists.
 *
 * Returns the minted id alongside nothing else — callers that need it should
 * read it from the `onCreated` path, which is what the apply layer actually
 * wrote rather than what we asked for.
 *
 * Same reporting contract as {@link submitTaskCreate}: never throws, and a
 * refusal reaches the user through §4.5's dispatch.
 */
export async function submitManualTaskCreate(
  args: ManualTaskCreateArgs,
): Promise<boolean> {
  const taskId = mintManualTaskId()
  return runCreate(
    { kind: 'task_create', task_id: taskId, content: args.content },
    taskId,
    { projectId: args.projectId, onCreated: args.onCreated, timeoutMs: args.timeoutMs },
  )
}

/** Everything both creates share — see the module docstring for what they don't. */
async function runCreate(
  payload: TaskCreatePayload,
  taskId: string,
  args: {
    projectId: string
    onCreated?: (taskPath: string) => void
    onConverged?: () => void
    timeoutMs?: number
  },
): Promise<boolean> {
  const { projectId } = args

  markPending(projectId, taskId)
  let unconfirmed = false
  let commandId = taskId

  try {
    const outcome = await submitTaskCommand(
      projectId,
      payload,
      {
        timeoutMs: args.timeoutMs,
        // Recording the pointer HERE rather than inheriting it from
        // `submitSequencedCommand`, which is where every other command gets one.
        // That function is the ledger's, and a create does not go through the
        // ledger — so without this line a create would be the one operation with
        // no reload recovery, and the one where losing it is most visible: the
        // user would see neither a proposal nor a board row, with nothing to say
        // why. `baseSeq` is deliberately absent rather than faked (§4.3).
        onQueued: (id) => {
          commandId = id
          rememberCommand({ projectId, taskId, commandId: id })
        },
      },
    )

    unconfirmed = outcome.status === 'unresolved'
    // Only once the outcome is KNOWN. An unresolved command keeps its pointer so
    // a later load can re-attach and finally say what happened (3.2c).
    if (!unconfirmed) forgetCommand(outcome.commandId)

    if (outcome.status === 'done') {
      args.onCreated?.(outcome.taskPath ?? `projects/${projectId}/tasks/${taskId}`)
      return true
    }

    if (unconfirmed) {
      // The command may still apply, so this is not reported as a failure — but
      // `onCreated` will NOT run, and that costs more here than on an edit.
      //
      // A missed follow-through on an edit costs telemetry. Here it means the
      // task can appear on the board while TraceX is never told about it: a
      // user-visible integration failure with no error surface anywhere. The
      // recount and the CSM events are skipped with it.
      //
      // Not reconciled here, deliberately — a create that reconciled its own
      // side effects would need to watch for a task it stopped waiting for.
      // Raised on 4.6 and 4.7 instead.
      logger.warn(
        `task create ${taskId}: unresolved - export, recount and CSM events skipped`,
      )
      return false
    }

    const verdict = reportOutcome(outcome)

    if (verdict.action === 'converge') {
      // `already_created`. Today this is simply success: the guard in
      // `check_target` fires whenever a task with this id exists, and until 4.2
      // un-approving DELETES the task, so the id is free and an accept after an
      // un-approve creates fresh.
      //
      // **4.2 breaks that coincidence.** Once un-approve keeps the task and its
      // trail, accepting a previously un-approved proposal lands here with the
      // task sitting in `unapproved` — and reporting success would tell the user
      // their accept worked while the task stays parked.
      //
      // So converge means "a task with this id exists", never "the state you
      // wanted holds". The lifecycle is what distinguishes them, and 4.3's
      // re-approve is the operation that actually revives it.
      logger.info(`task create ${taskId}: already exists, converging`)
      args.onConverged?.()
      args.onCreated?.(`projects/${projectId}/tasks/${taskId}`)
      return true
    }

    if (verdict.action === 'conflict') {
      // Unreachable: `stale` needs a `base_seq` and this command has none. Left
      // as a floor rather than an assertion, because `reportOutcome` toasts
      // nothing for a conflict — so if the apply layer ever did answer `stale`
      // here, the silent path would be the user's accept vanishing with no
      // explanation. Exactly the failure 3.2c had to close for recovered
      // conflicts, and the same answer: say something rather than nothing.
      useAppStore().toast(t('common.error'), t('tasks.errors.command.generic'), 'error')
    }

    return false
  } catch (e) {
    if (isNotQueued(e)) {
      // Nothing was written, so nothing will ever apply it. The proposal is
      // untouched — there was no optimistic change to undo — but the user
      // clicked accept and nothing happened, which needs saying.
      logger.error(`task create ${taskId}: never queued`, e)
    } else {
      logger.error(`task create ${taskId}: failed after submission`, e)
    }
    useAppStore().toast(t('common.error'), t('tasks.errors.command.generic'), 'error')
    return false
  } finally {
    // `-1` is the anchor for an unresolved create, and it is not arbitrary.
    //
    // `reconcilePending` clears the notice once the task's `last_revision_seq`
    // passes the anchor. A created task's genesis revision is `seq 0`, so `-1`
    // clears the moment the task exists at all — which is exactly when the
    // uncertainty ends. Anchoring at `0` instead would hold the warning until
    // somebody edited the task, long after the answer arrived.
    releasePending(projectId, taskId, commandId, unconfirmed ? -1 : undefined)
  }
}
